import { CSSProperties, ReactNode } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Icon } from 'components/common/icons';

interface Props {
  children: ReactNode;
  horizontalBreakpoint?: number;
  horizontalSpacing?: number;
  label?: string | React.ReactNode;
  labelHelpText?: string;
  labelStyle?: CSSProperties;
  spacing?: number;
  style?: CSSProperties;
  tooltip?: string;
  verticalLabelSpacing?: number;
}

type DependantProps =
  | { horizontal?: false; labelHelpText?: string }
  | { horizontal: true; labelHelpText?: never };

const ContentLabel = ({
  children,
  horizontal,
  horizontalBreakpoint = breakpoints.small,
  horizontalSpacing = 50,
  label,
  labelHelpText,
  labelStyle,
  spacing = 20,
  style,
  tooltip,
  verticalLabelSpacing = 10
}: Props & DependantProps) => {
  const { theme } = useReduxState();

  return (
    <div
      className={`content-label ${
        horizontal ? 'content-label-horizontal' : ''
      }`}
      style={style}
    >
      <div className="label" style={labelStyle}>
        <span
          {...(tooltip
            ? {
                'aria-label': tooltip,
                'data-tooltip': tooltip
              }
            : {})}
        >
          <span style={{ marginRight: tooltip ? 5 : 0 }}>{label}</span>

          {tooltip ? (
            <Icon icon="question-circle" style={{ fontSize: '1.3rem' }} />
          ) : null}
        </span>

        {labelHelpText ? (
          <div className="content-label-help-text">{labelHelpText}</div>
        ) : null}
      </div>

      <div
        className="value"
        style={horizontal ? {} : { marginTop: verticalLabelSpacing }}
      >
        {children}
      </div>

      <style jsx>{`
        .content-label {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
          padding: 0;
        }
        .content-label:not(:first-child) > .label,
        .content-label-horizontal:not(:first-child) > .label {
          padding: ${`${spacing}px 0 0`};
        }
        .content-label-horizontal > .value {
          padding: ${`${verticalLabelSpacing}px 0 0`};
        }
        .content-label-help-text {
          color: ${theme.textColorLighter};
          display: block;
          font-size: 1.4rem;
          font-weight: normal;
          margin-top: 2px;
        }
        @media (min-width: ${horizontalBreakpoint}px) {
          .content-label-horizontal {
            display: table-row;
          }
          .content-label-horizontal > .label {
            display: table-cell;
            max-width: 280px;
            padding: ${`0 ${horizontalSpacing}px 0 0`};
          }
          .content-label-horizontal > .value {
            display: table-cell;
            padding: 0;
          }
          .content-label-horizontal:not(:first-child) > .label {
            padding: ${`${spacing}px ${horizontalSpacing}px 0 0`};
          }
          .content-label-horizontal:not(:first-child) > .value {
            padding: ${`${spacing}px 0 0`};
          }
        }
      `}</style>
    </div>
  );
};

export default ContentLabel;
